import './theme.css';
import type { LocalizedNavItem } from '@/app/lib/stage/nav-model.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import type { FC } from 'react';
import { defineMessages, type MessageDescriptor } from 'react-intl';
import { FieldDocHeader } from './FieldDocHeader.tsx';

const siteMessages = defineMessages({
  diagnoseLabel: {
    id: 'nav.diagnose.label',
    defaultMessage: 'Crop problem',
    description: 'Short top-navigation destination label for the crop diagnosis flow.',
  },
  diagnoseIntent: {
    id: 'nav.diagnose.intent',
    defaultMessage: 'I want a diagnosis for my crop',
    description: 'Message sent to the agent when the farmer chooses Crop problem in the navigation.',
  },
  weatherLabel: {
    id: 'nav.weather.label',
    defaultMessage: 'Weather',
    description: 'Short top-navigation destination label for the live farm weather and field advice.',
  },
  weatherIntent: {
    id: 'nav.weather.intent',
    defaultMessage: 'Show me the weather for my farm and what I should do',
    description: 'Message sent to the agent when the farmer chooses Weather in the navigation.',
  },
  seasonLabel: {
    id: 'nav.season.label',
    defaultMessage: 'My season',
    description: 'Short top-navigation destination label for the record of the running crop season.',
  },
  seasonIntent: {
    id: 'nav.season.intent',
    defaultMessage: 'Show me the record of my current crop',
    description: 'Message sent to the agent when the farmer chooses My season in the navigation.',
  },
  visitLabel: {
    id: 'nav.visit.label',
    defaultMessage: 'Field visit',
    description: 'Short top-navigation destination label for booking an agronomist field visit.',
  },
  visitIntent: {
    id: 'nav.visit.intent',
    defaultMessage: 'I want an agronomist to come to my field',
    description: 'Message sent to the agent when the farmer chooses Field visit in the navigation.',
  },
  photoChip: {
    id: 'home.chip.photo',
    defaultMessage: 'I have a photo of the leaf',
    description: 'Suggested first message: the farmer already has a photo to send.',
  },
  sprayChip: {
    id: 'home.chip.spray',
    defaultMessage: 'Can I spray today?',
    description: 'Suggested first message: the farmer asks whether the weather allows spraying.',
  },
  weekChip: {
    id: 'home.chip.week',
    defaultMessage: 'What should I do this week?',
    description: "Suggested first message: the farmer wants this week's field work for their crop.",
  },
  askPlaceholder: {
    id: 'composer.clinicPrompt',
    defaultMessage: 'Ask anything about your crop, or send a photo…',
    description: 'Invitation shown inside the empty message composer of the farm assistant.',
  },
});

export interface SiteConfig {
  brandName: string;
  navItems: LocalizedNavItem[];
  defaultChips: MessageDescriptor[];
  /** Page-area mode: 'site' = the latest surface IS the page; 'chat' = a
   *  scrolling transcript with inline surfaces. Same shell either way. */
  mode: 'site' | 'chat';
  /** Site header (brand + menu). Off = minimal canvas: the rail keeps
   *  history, chips keep the standing intents, the dock keeps input.
   *  Template default is off; a branded site turns it on. */
  showHeader: boolean;
  /** Custom header component. Omit for the template's default header
   *  (monogram + name + menu); provide one for a bespoke composition —
   *  it receives resolved nav items + onNavigate, so menu mechanics and
   *  the agent's /nav overlay keep working. */
  Header?: FC<AgentHeaderProps>;
  /** First-load pending state shows the brand name (shimmer) instead of the
   *  neutral text skeleton — for agents with a website identity. */
  brandedArrival?: boolean;
  /** The site's designed look — a builder decision, like a real website's.
   *  'auto' follows the visitor's OS. */
  appearance: 'light' | 'dark' | 'auto';
  /** Omnibox placeholder override. */
  placeholder?: MessageDescriptor;
}

export const SITE_CONFIG: SiteConfig = {
  brandName: 'FieldDoc Krishi Kendra',
  navItems: [
    { label: siteMessages.diagnoseLabel, intent: siteMessages.diagnoseIntent },
    { label: siteMessages.weatherLabel, intent: siteMessages.weatherIntent },
    { label: siteMessages.seasonLabel, intent: siteMessages.seasonIntent },
    { label: siteMessages.visitLabel, intent: siteMessages.visitIntent },
  ],
  defaultChips: [siteMessages.photoChip, siteMessages.sprayChip, siteMessages.weekChip],
  mode: 'site',
  showHeader: true,
  Header: FieldDocHeader,
  brandedArrival: true,
  appearance: 'light',
  placeholder: siteMessages.askPlaceholder,
};
